import React, { useState, forwardRef } from "react";
import { motion } from 'framer-motion';
import { useTranslation } from "react-i18next";
import "../businessExpansionPage/BusinessExpansionHeroSection.css";
import "./inventoryFinancing.css";
import { useNavigate } from "react-router-dom";
import { CREDIT_API_URL } from "../../constants";
import { fadeInUp, buttonVariants } from '../../utility/framerMotionAnimations';
import NotificationPopup from "../../utility/NotificationPopup";

const initialInvoiceDetails = {
  invoiceNumber: "",
  invoiceDate: "",
  dueDate: "",
  irnNumber: "",
  totalAmount: "",
  sellerGST: "",
  sellerPAN: "",
  buyerGST: "",
  buyerPAN: "",
};

const InventoryFinancingHeroSection = forwardRef((props, ref) => {
  const { t } = useTranslation("inventoryFinancingPage");
  const navigate = useNavigate();

  const [step, setStep] = useState(0); // 0: Upload, 1: Verify
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({ invoice: false });
  const [isProcessing, setIsProcessing] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  // Remove invoiceDetails, consent, and step logic for verification UI

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setInvoiceFile(file);
      setValidationErrors((prev) => ({ ...prev, invoice: false }));
      setErrorMessage("");
      setShowPreview(false);
    }
  };

  // Handle click on upload container
  const handleUploadContainerClick = () => {
    if (!invoiceFile) {
      document.getElementById('invoiceUpload').click();
    }
  };

  // Handle delete file
  const handleDeleteFile = () => {
    setInvoiceFile(null);
    setShowPreview(false);
    // Reset the file input to allow uploading the same file again
    const fileInput = document.getElementById('invoiceUpload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Helper to check if file is image
  const isImageFile = (file) => {
    if (!file) return false;
    const imageTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp", "image/gif", "image/svg+xml"];
    return imageTypes.includes(file.type);
  };

  // Helper to check if file is PDF
  const isPdfFile = (file) => {
    return file && file.type === "application/pdf";
  };

  // Handle view button click
  const handleViewClick = () => {
    if (invoiceFile) {
      if (isImageFile(invoiceFile)) {
        setShowImageModal(true);
      } else if (isPdfFile(invoiceFile)) {
        const pdfUrl = URL.createObjectURL(invoiceFile);
        window.open(pdfUrl, '_blank');
      } else {
        const fileUrl = URL.createObjectURL(invoiceFile);
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = invoiceFile.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  // Step 1: Upload and process invoice
  const handleStartApp = async () => {
    const errors = { invoice: !invoiceFile };
    setValidationErrors(errors);
    if (errors.invoice) {
      setErrorMessage("Please upload your invoice file.");
      return;
    }
    setErrorMessage("");
    setIsProcessing(true);
    const formData = new FormData();
    formData.append('file', invoiceFile);
    try {
      const response = await fetch(`${CREDIT_API_URL}invoices/gst/upload`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        throw new Error('File upload failed');
      }
      const result = await response.json();
      if (result.status === "Success" && result.data && result.data.invoiceDetails) {
        const applicationStage = result.data.applicationStage;
        const documentId = result.data.documentId;
        const invoiceDetails = result.data.invoiceDetails;
        const leadId = result.data.leadId;
        const anchorId = result.data.anchorId;
        
        // Handle different application stages
        if (applicationStage === "leadCreated") {
          // Clear previous application state before starting new application
          try {
            localStorage.removeItem('inventoryFinancingApplicationState');
            localStorage.removeItem('inventoryFinancingDocumentId');
          } catch (error) {
            console.error('Error clearing localStorage:', error);
          }
          
          // Save documentId to localStorage
          if (documentId) {
            try {
              localStorage.setItem('inventoryFinancingDocumentId', documentId);
            } catch (error) {
              console.error('Error saving documentId to localStorage:', error);
            }
          }
          
          // Navigate to step 3 (loan offer step) with all necessary data
          navigate("/inventory-financing/application", { 
            state: { 
              invoiceDetails: invoiceDetails,
              documentId: documentId,
              leadId: leadId,
              anchorId: anchorId,
              applicationStage: applicationStage,
              isNewApplication: true,
              skipToStep: 3 // Flag to skip to step 3
            }
          });
        } else if (applicationStage === "loanOfferChecked") {
          // Show notification popup for application under process
          setShowNotification(true);
        } else {
          // Default behavior for new applications
          // Clear previous application state before starting new application
          try {
            localStorage.removeItem('inventoryFinancingApplicationState');
            localStorage.removeItem('inventoryFinancingDocumentId');
          } catch (error) {
            console.error('Error clearing localStorage:', error);
          }
          
          // Navigate to application page, passing invoiceDetails and documentId
          navigate("/inventory-financing/application", { 
            state: { 
              invoiceDetails: invoiceDetails,
              documentId: documentId,
              leadId: leadId,
              anchorId: anchorId,
              isNewApplication: true // Flag to indicate this is a new application
            }
          });
        }
      } else {
        setErrorMessage(result.message || 'Invoice processing failed');
      }
    } catch (error) {
      setErrorMessage(error.message || 'File upload failed');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <section ref={ref} className="inventory-financing-hero-section" style={{ minHeight: "92vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="business-hero-wrapper" style={{ justifyContent: "center", alignItems: "center", display: "flex", width: "100%", minHeight: "70vh" }}>
        <motion.div 
          className="inventory-financing-hero-card"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2, margin: "0px 0px -100px 0px" }}
          variants={fadeInUp}
        >
          <motion.h2
            className="inventory-financing-hero-title"
            variants={fadeInUp}
          >
            <span className="business-hero-title-primary">Are you a supplier?</span>
            <span className="business-hero-title-highlight">
              Get credit on your invoice
            </span>
          </motion.h2>

          <div className="business-form-section">
            <div className="business-form-section-bottom">
              <div className="business-form">
                <div className="business-form-content">
                  <div 
                    className={`inventory-financing-input-field-container${validationErrors.invoice ? " inventory-financing-input-field-error" : ""}${invoiceFile ? " inventory-financing-has-file" : ""}`} 
                    style={{ position: "relative", cursor: invoiceFile ? "default" : "pointer" }}
                    onClick={handleUploadContainerClick}
                  >
                    <input id="invoiceUpload" type="file" accept=".pdf,.jpeg,.jpg,.png" onChange={handleFileUpload} className="business-input-field" style={{ display: "none" }} />
                    <span 
                      style={{ 
                        fontSize: 14, 
                        color: "#20386F", 
                        marginRight: "auto", 
                        overflow: "hidden", 
                        textOverflow: "ellipsis", 
                        whiteSpace: "nowrap", 
                        maxWidth: 180,
                        cursor: invoiceFile ? "default" : "pointer",
                        paddingTop: invoiceFile ? "8px" : "0",
                        pointerEvents: invoiceFile ? "auto" : "none"
                      }} 
                      onClick={(e) => {
                        if (!invoiceFile) {
                          e.stopPropagation();
                          document.getElementById('invoiceUpload').click();
                        }
                      }}
                    >
                      {invoiceFile ? invoiceFile.name : ""}
                    </span>
                    <div className="business-hero-placeholder" style={{ position: "absolute", left: "2%"}}>Upload Your Invoice</div>
                    {!invoiceFile && (
                      <button 
                        type="button" 
                        onClick={(e) => {
                          e.stopPropagation();
                          document.getElementById('invoiceUpload').click();
                        }} 
                        style={{ background: "none", border: "none", cursor: "pointer", marginLeft: "auto", padding: 0, display: "flex", alignItems: "center", pointerEvents: "auto" }} 
                        aria-label="Upload Invoice"
                      >
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: "#20386F" }}>
                          <path d="M9 16V10H5L12 3L19 10H15V16H9ZM5 20V18H19V20H5Z" fill="#20386F"/>
                        </svg>
                      </button>
                    )}
                    {invoiceFile && (
                      <div 
                        style={{ display: "flex", alignItems: "center", marginLeft: "auto", gap: 8, pointerEvents: "auto" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button type="button" onClick={handleViewClick} style={{ background: "#20386F", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", padding: "8px 16px", fontSize: 12, fontWeight: 500, width: 70, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }} aria-label="View Invoice">View</button>
                        <button type="button" onClick={handleDeleteFile} style={{ background: "#D32F2F", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", padding: "8px 16px", fontSize: 12, fontWeight: 500, width: 70, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }} aria-label="Delete Invoice">Delete</button>
                      </div>
                    )}
                  </div>
                  {showPreview && invoiceFile && isImageFile(invoiceFile) && (
                    <div style={{ margin: "16px auto", width: "60%", textAlign: "center" }}>
                      <img src={URL.createObjectURL(invoiceFile)} alt="Invoice Preview" style={{ maxWidth: "100%", maxHeight: 320, borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }} />
                    </div>
                  )}
                  {showImageModal && invoiceFile && isImageFile(invoiceFile) && (
                    <div style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      width: '100vw',
                      height: '100vh',
                      background: 'rgba(0,0,0,0.6)',
                      zIndex: 10000,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                      onClick={() => setShowImageModal(false)}
                    >
                      <div style={{ position: 'relative', background: '#fff', borderRadius: 12, boxShadow: '0 2px 16px rgba(0,0,0,0.18)', padding: 16 }} onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => setShowImageModal(false)}
                          style={{ position: 'absolute', top: 8, right: 8, background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: '#20386F' }}
                          aria-label="Close"
                        >×</button>
                        <img src={URL.createObjectURL(invoiceFile)} alt="Invoice Preview" style={{ maxWidth: '80vw', maxHeight: '80vh', borderRadius: 8 }} />
                      </div>
                    </div>
                  )}
                  {isProcessing && (
                   <div style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      width: '100vw',
                      height: '100vh',
                      backdropFilter: 'blur(10px)',   // <-- blur background
                      background: 'rgba(0,0,0,0.3)',  // optional: dark transparent overlay
                      zIndex: 9999,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                      <div className="circular-loader" style={{ width: 64, height: 64, border: '8px solid #eee', borderTop: '8px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                      <div style={{ color: '#fff', fontSize: 22, fontWeight: 600, marginTop: 24 }}>Fetching Invoice Details...</div>
                    </div>
                  )}
                  <div className="inventory-financing-input-field-hint-container" style={{ marginTop: -12, marginBottom: 12 }}>
                    <div className="field-hint" style={{ whiteSpace: "wrap", textAlign: "center" }}>You can upload a PDF, JPEG, JPG, or PNG copy of your invoice which is due for payment from your buyer. <br/>Maximum file size should be 5 MB.</div>
                    {validationErrors.invoice && (<div className="field-error">Please upload your invoice file.</div>)}
                  </div>
                  {errorMessage && !validationErrors.invoice && (<div className="business-validation-error">{errorMessage}</div>)}
                  <motion.button 
                    className="business-start-application-btn" 
                    onClick={handleStartApp} 
                    disabled={isProcessing || !invoiceFile} 
                    style={{ width: "60%", alignSelf: "center" }}
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                  >
                    {isProcessing ? "Processing..." : t("inventory-financing-hero-section-button") || "Start Application"}
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Notification Popup for Application Under Process */}
      <NotificationPopup
        show={showNotification}
        message={
          <>
            Your application is under review. <br />
            We will notify you once reviewed.
          </>
        }
        type="success"
        customIcon={<img src="/images/review-icon.png" alt="Review" width={32} height={32}/>}
        customButton={
          <button
            className="business-start-application-btn"
            onClick={() => {
              setShowNotification(false);
            }}
            style={{ width: 'fit-content', minWidth: '200px', marginTop: '24px' }}
          >
            OK
          </button>
        }
        onClose={() => {
          setShowNotification(false);
        }}
        autoClose={false}
      />
    </section>
  );
});

export default InventoryFinancingHeroSection;

/* Add keyframes for spin animation */
<style>
{`
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`}
</style>
